import { Injectable, inject } from '@angular/core';
import { Auth, type User } from '../../../app/core/firebase/auth';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import type {
  StreamChannelAddModeratorsPayload,
  StreamChannelDemoteModeratorsPayload,
  StreamCreateUserPayload,
  StreamUpdateUserPayload,
  StreamUser,
} from '../../../app/types/chat';
import { environment } from '../../../environments/environment';
import { StatusRes } from '../../../app/types/rest-api';

@Injectable({
  providedIn: 'root',
})
export class StreamUserService {
  private auth = inject(Auth);
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/stream-chat`;

  public currentUser(): User {
    return this.auth.currentUser!;
  }

  public getToken(_uid?: string): Observable<string> {
    return this.http
      .post<{ token: string }>(`${this.baseUrl}/token`, {})
      .pipe(map(res => res.token));
  }

  public createUser(
    user: Omit<StreamCreateUserPayload['user'], 'id'>
  ): Observable<StreamUser> {
    const id = this.currentUser().uid;
    return this.http.post<StreamUser>(`${this.baseUrl}/users`, { id, ...user });
  }

  public logout(): Observable<StatusRes> {
    return this.http.post<StatusRes>(`${this.baseUrl}/revoke-token`, {});
  }

  public updateUser(
    user: Omit<StreamUpdateUserPayload['user'], 'id'>
  ): Observable<StreamUser> {
    const uid = this.currentUser().uid;
    return this.http.put<StreamUser>(`${this.baseUrl}/users`, { uid, user });
  }

  public addModerators(
    cid: StreamChannelAddModeratorsPayload['cid'],
    members: StreamChannelAddModeratorsPayload['members']
  ): Observable<StatusRes> {
    return this.http.post<StatusRes>(`${this.baseUrl}/channels/moderators`, {
      cid,
      members,
    });
  }

  public demoteModerators(
    cid: StreamChannelDemoteModeratorsPayload['cid'],
    members: StreamChannelDemoteModeratorsPayload['members']
  ): Observable<StatusRes> {
    return this.http.delete<StatusRes>(`${this.baseUrl}/channels/moderators`, {
      body: { cid, members },
    });
  }
}
